<template>
	<div class="new-schedule">
		<el-form :inline="true">
			<el-form-item>
				<el-select
					v-if="!showMultiProviders || patientProviderProfileList.length === 1"
					v-model="event"
					placeholder="Select event"
					style="width:250px"
					size="small"
					filterable>
					<el-option v-for="event in orderedEvents"
						:key="event.id"
						:label="event.name"
						:value="event.id">
					</el-option>
				</el-select>
				<el-cascader
					v-else
					class="provider-cascader"
					v-model="selectedProviderEvent"
					:options="multiProviderEventList"
					 @change="handleEventChange"
					>
				</el-cascader>
			</el-form-item>
			<el-form-item>
				<el-date-picker
					v-model="time"
					type="date"
					format="MMM d, yyyy"
					placeholder="Select date of the event"
					style="width:250px"
					size="small"
					range-separator="/">
				</el-date-picker>
			</el-form-item>
			<el-form-item>
				<el-button type="primary" size="small"
					:disabled="! valid || scheduling"
					@click="create">
					<strong>Schedule Event</strong>
				</el-button>
			</el-form-item>
		</el-form>

		<el-dialog title="Past Messages"
			:append-to-body="true"
			:close-on-click-modal="false"
			:visible.sync="selectPastMessages">
			<p style="margin-top:-30px;">Select the messages that should be sent immediately to this patient.</p>
			<br>
			<PastMessage
				:time="time"
				:message="pastMessages"
				:event="selectedEvent"
				@confirm="createAndSend"
				@cancel="cancelCreate">
			</PastMessage>
		</el-dialog>
	</div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex'
import format from '@/module/util/format'

import PastMessage from './message/Past'
import profile from '@/module/filter/predicate/profile'

export default {
	name: 'patient-schedule-card-form',

	props: {
		patient: {
			type: Object,
			required: true
		}
	},

	data() {
		return {
			event: null,
			time: null,
			// Whether to show the past messages dialog, and the selected past messages
			selectPastMessages: false,
			pastMessage: {},
			selectedProviderEvent: [],
			selectedProvider: null
		}
	},

	mounted() {
		// setTimeout(() => {
		// 	this.selectPastMessages = true
		// }, 1000)
		if(this.patientProviderProfileList.length === 1)
			this.selectedProvider = this.patientProviderProfileList[0].provider
		
	},

	methods: {
		...format,
		...mapActions('provider', [
			'createPatientSchedule'
		]),
		/**
		 * @func create
		 * @desc Creates a new schedule for the patient.
		 */
		create() {
			var time = new Date(this.time).setHours(12)
			time -= new Date(this.time).getTimezoneOffset() * 60000

			if (this.pastMessages.length > 0) {
				this.selectPastMessages = true
			}
			else this.createPatientSchedule({
				patient: this.patient.id,
				event: this.event,
				time,
				provider: this.selectedProvider
			})
			.then(() => {
				this.reset()
			})
		},
		/**
		 * @func create
		 * @desc Creates a new schedule for the patient and send the specified past messages.
		 */
		createAndSend(send) {
			this.selectPastMessages = false

			var time = new Date(this.time).setHours(12)
			time -= new Date(this.time).getTimezoneOffset() * 60000

			this.createPatientSchedule({
				patient: this.patient.id,
				event: this.event,
				send,
				time,
				provider: this.selectedProvider
			})
			.then(() => {
				this.reset()
			})
		},
		handleEventChange(selectedProviderEvent) {
			if(Array.isArray(selectedProviderEvent) && selectedProviderEvent.length == 2) {
				this.selectedProvider = selectedProviderEvent[0].split(':')[0]
				this.event = selectedProviderEvent[1].split(':')[0]
			}
				
		},

		cancelCreate() {
			this.selectPastMessages = false
		},

		reset() {
			this.event = null
			this.time = null
			this.selectedProviderEvent = []
		}
	},

	computed: {
		...mapGetters('provider', [
			'getPatientSchedulingState',
			'getProviderProfileList',
			'allPatientsChosen',
			'getProviderList',
			'getProviderNameById',
			'getProvidersIdArray',
			'getPatientProviderProfileList'
		]),
		...mapGetters('content', [
			'getContent',
			'getContentKey',
			'getProfileContent'
		]),
		patientProviderProfileList() {
			return this.getPatientProviderProfileList(this.patient.id)
		},
		events() {
			if(this.patientProviderProfileList)
				return this.patientProviderProfileList.flatMap(({ profile }) => {
					return (this.getContentKey('profile', profile, 'event') || [])
						.map((event) => (typeof event == 'string') ? event : event.id)
						.map((id) => this.getContent('event', id))
						.filter((e) => e != null && e.name)
				})
			return []
		},
		orderedEvents() {
			return this.events.slice().sort((a, b) => {
				return a.name.toLowerCase().localeCompare(b.name.toLowerCase())
			})
		},
		selectedEvent() {
			return this.events.find((event) => event.id == this.event)
		},
		pastMessages() {
			if (! this.event || ! this.time) return null

			var now = Date.now()
			var time = new Date(this.time).getTime()

			var event = this.events.find((e) => e.id == this.event)
			if (event && Array.isArray(event.message))
				return event.message.filter((message) => (time + message.delay < now))
			return []
		},
		scheduling() {
			return this.getPatientSchedulingState(this.patient.id)
		},
		valid() {
			return this.event != null && this.time != null
		},
		showMultiProviders() {
			return this.allPatientsChosen && this.patient && this.patient.hasOwnProperty('provider') && (this.patient.provider.length > 1)
		},
		multiProviderEventList() {
			try {
				let arr = this.patient.profile_map
					.filter((providerProfile) => this.getProvidersIdArray.includes(providerProfile.split(':')[0]))
					.map((providerProfile) => {
						const [providerId, profileId ] = providerProfile.split(':')
						const value = providerProfile,
							label = `${this.getProviderNameById(providerId)} (${this.getContentKey('profile', profileId, 'title')})`
						const children = this.getProfileContent(profileId, 'event')
							.filter((event) => !event.archived)
							.map(({ id, name }) => ({ value: id + ':' + value, label: name }))
						return {
							value,
							label,
							children
						}
					})
					// sort
					try {
						arr.sort((a, b) => a.label.trim() > b.label.trim() ? 1 : -1)
						arr.forEach(el => {
							if (el.hasOwnProperty('children') && el.children && el.children.length > 1)
								el.children.sort((a, b) => a.label.trim() > b.label.trim() ? 1 : -1)
						})
					} catch (error) {
						console.error('Problem sorting multiProviderEventList ', arr)
					}

					return arr
			} catch (error) {
				console.error('Problem Calculating multiProviderEventList ', error)
				return []
			}
		}
	},

	components: {
		PastMessage
	}
}
</script>

<style lang="scss" scoped>
.new-schedule {
	padding: 10px 10px 15px 10px;
	height: 60px;
	border-bottom: solid 1px #f0f0f0;

	.el-button {
		font-size: 15px;

	}
}
</style>

<style lang="scss">
.provider-cascader.el-cascader {
	height: 38px;
	line-height: 38px;
	width: 250px;
	.el-input__inner {
		height: 36px;
		line-height: 36px;
		font-size: 14px;
	}
}
</style>
